// =============================================
// src/theme.tsx
// =============================================
import React, { createContext, useMemo, useState, ReactNode } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

export const THEME_TYPES = {
  LIGHT: 'light',
  DARK: 'dark'
}

export const ColorModeContext = createContext({
  toggleColorMode: () => { },
});

export const getDesignTokens = (mode: 'light' | 'dark') =>
  createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
          primary: { main: '#1e3a8a' },
          background: { default: '#f8f4eb', paper: '#ffffff' },
          text: { primary: '#1e2937', secondary: '#475569' },
          customBackground: `
              radial-gradient(circle at 70% 40%, rgba(59,130,246,0.12) 0%, transparent 45%),
              radial-gradient(circle at 10% 10%, rgba(0,0,0,0.03) 0%, transparent 40%),
              linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 50%, #E5EAF1 100%)`

        }

        : {
          primary: { main: '#3b82f6' },
          background: { default: '#0a1428', paper: '#1e2937' },
          text: { primary: '#f1f5f9', secondary: '#cbd5e1' },
          customBackground: `
                  radial-gradient(circle at 65% 50%, rgba(59,130,246,0.18) 0%, transparent 45%),
                  radial-gradient(circle at 20% 20%, rgba(255,255,255,0.04) 0%, transparent 40%),
                  linear-gradient(135deg, #0A0F1C 0%, #0B1220 40%, #05070D 100%)
              
              `,
        }),
    },

    typography: {
      fontFamily: '"Inter", "Roboto", sans-serif',
      h1: { fontSize: '3.5rem', fontWeight: 700, lineHeight: 1.1 },
      h4: { fontWeight: 600 },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 50, fontWeight: 600 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 24 },
        },
      },
    },
  });

// Theme Provider Wrapper
export const ThemeProviderWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return 'light';
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    []
  );

  const theme = useMemo(() => getDesignTokens(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};